import React, { useState, useRef } from "react";
import { Modal, Button, message, Tag, Typography, Divider, Alert } from "antd";
import {
  CopyOutlined,
  CheckOutlined,
  MailOutlined,
  BankOutlined,
  UserOutlined,
  CalendarOutlined,
  DollarOutlined,
  SafetyCertificateOutlined,
  InfoCircleOutlined,
} from "@ant-design/icons";

const { Text, Title } = Typography;

const EmailQuoteModal = ({
  open,
  onClose,
  payload,
  lead,
  days = [],
  pricing = {},
  companyProfile = null,
  companySettings = null,
  quotationSettings = {},
}) => {
  const [copied, setCopied] = useState(false);
  const containerRef = useRef(null);

  // Extract Consultant / Assigned User Details
  const consultant = {
    name:
      lead?.assignedTo?.name ||
      lead?.createdBy?.name ||
      payload?.consultant?.name ||
      "Travel Specialist",
    email:
      lead?.assignedTo?.email ||
      lead?.createdBy?.email ||
      payload?.consultant?.email ||
      "",
    phone:
      lead?.assignedTo?.phone ||
      lead?.createdBy?.phone ||
      payload?.consultant?.phone ||
      "",
  };

  // Extract Company Details
  const companyName =
    companyProfile?.displayName ||
    companyProfile?.officialName ||
    companySettings?.companyName ||
    "Our Travel Agency";
  const companyEmail =
    companyProfile?.email || companySettings?.email || consultant.email || "";
  const companyPhone =
    (Array.isArray(companyProfile?.supportPhoneNumbers) &&
      companyProfile.supportPhoneNumbers.filter(Boolean).join(", ")) ||
    companySettings?.phone ||
    consultant.phone ||
    "";
  const companyWebsite =
    companyProfile?.website || companySettings?.website || "";
  const companyAddress = [
    companyProfile?.address?.street,
    companyProfile?.address?.city,
    companyProfile?.address?.state,
    companyProfile?.address?.country,
    companyProfile?.address?.zipCode,
  ]
    .filter(Boolean)
    .join(", ");

  // Destination name(s)
  const destName =
    Array.isArray(lead?.destinations) && lead.destinations.length > 0
      ? lead.destinations.map((d) => d.name || d.city || d).join(", ")
      : payload?.destination?.name ||
        lead?.destinationSnapshot ||
        "Your Selected Destination";

  // Default Bank Account
  const defaultBank =
    companyProfile?.bankAccounts?.find((b) => b.isDefault) ||
    companyProfile?.bankAccounts?.[0] ||
    null;

  // Inclusions & Exclusions
  const inclusions =
    payload?.meta?.inclusions || quotationSettings?.inclusions || [];
  const exclusions =
    payload?.meta?.exclusions || quotationSettings?.exclusions || [];

  // Terms & Policies
  const terms =
    companySettings?.termsAndConditions?.content ||
    companyProfile?.termsAndConditions ||
    quotationSettings?.terms ||
    "";
  const paymentPolicy =
    companySettings?.paymentPolicy ||
    companyProfile?.paymentPolicy ||
    quotationSettings?.paymentPolicy ||
    "";
  const refundPolicy =
    companySettings?.refundPolicy ||
    companyProfile?.refundPolicy ||
    quotationSettings?.cancellationPolicy ||
    "";

  // Build Plain Text String for clipboard fallback
  const generatePlainText = () => {
    let txt = `====================================================\n`;
    txt += `${companyName.toUpperCase()} - TOUR PROPOSAL\n`;
    txt += `====================================================\n\n`;

    txt += `Dear ${payload?.customerName || "Valued Guest"},\n\n`;
    txt += `Warm Greetings from ${companyName}!\n`;
    txt += `Thank you for reaching out to us. We are delighted to present a customized travel itinerary for your upcoming trip to ${destName}.\n\n`;

    txt += `----------------------------------------------------\n`;
    txt += `1. JOURNEY OVERVIEW\n`;
    txt += `----------------------------------------------------\n`;
    txt += `• Proposal Ref: ${payload?.proposalId || "Q-" + Date.now().toString().slice(-6)}\n`;
    txt += `• Guest Name: ${payload?.customerName || "-"}\n`;
    txt += `• Destination(s): ${destName}\n`;
    txt += `• Duration: ${payload?.durationText || (days.length ? `${days.length - 1} Nights / ${days.length} Days` : "-")}\n`;
    txt += `• Travel Date: ${payload?.dateRange?.[0] || lead?.travelDate?.slice(0, 10) || "As per request"}\n`;
    txt += `• Passengers: ${payload?.travelers?.adults || 1} Adults${payload?.travelers?.children ? `, ${payload.travelers.children} Children` : ""}${payload?.travelers?.infants ? `, ${payload.travelers.infants} Infants` : ""}\n`;
    txt += `• Package Grand Total: ₹${Number(pricing?.grandTotal || 0).toLocaleString("en-IN")}\n\n`;

    // Hotels
    txt += `----------------------------------------------------\n`;
    txt += `2. ACCOMMODATION DETAILS\n`;
    txt += `----------------------------------------------------\n`;
    let hasHotels = false;
    days.forEach((day, idx) => {
      if (day.hotels && day.hotels.length > 0) {
        day.hotels.forEach((h) => {
          hasHotels = true;
          txt += `• Day ${idx + 1} (${day.city || day.title || "City"}): ${h.name || h.hotelName || "Hotel"} (${h.starRating || "3"}★) | Room: ${h.roomCategory || h.roomType || "Standard"} | Meal Plan: ${h.mealPlan || "CP"}\n`;
        });
      }
    });
    if (!hasHotels) txt += `• Accommodation as selected / As per itinerary.\n`;
    txt += `\n`;

    // Day-wise Itinerary & Vehicles
    txt += `----------------------------------------------------\n`;
    txt += `3. DAY-WISE ITINERARY & TRANSPORT\n`;
    txt += `----------------------------------------------------\n`;
    days.forEach((day, idx) => {
      txt += `Day ${idx + 1}: ${day.title || "Day " + (idx + 1)} ${day.city ? `(${day.city})` : ""}\n`;
      if (day.notes) txt += `  Overview: ${day.notes}\n`;
      if (day.transport && day.transport.length > 0) {
        day.transport.forEach((t) => {
          const veh = t.vehicle || t.vehicleType || "Private Cab";
          txt += `  Transport: ${t.routeName || (t.source && t.destination ? `${t.source} to ${t.destination}` : "Local Sightseeing & Transfer")} [Vehicle: ${veh}]\n`;
        });
      }
      txt += `\n`;
    });

    // Inclusions
    if (inclusions.length > 0) {
      txt += `----------------------------------------------------\n`;
      txt += `4. PACKAGE INCLUSIONS\n`;
      txt += `----------------------------------------------------\n`;
      inclusions.forEach((inc) => {
        txt += `• ${inc}\n`;
      });
      txt += `\n`;
    }

    // Exclusions
    if (exclusions.length > 0) {
      txt += `----------------------------------------------------\n`;
      txt += `5. PACKAGE EXCLUSIONS\n`;
      txt += `----------------------------------------------------\n`;
      exclusions.forEach((exc) => {
        txt += `• ${exc}\n`;
      });
      txt += `\n`;
    }

    // Bank Details
    if (defaultBank) {
      txt += `----------------------------------------------------\n`;
      txt += `6. BANK PAYMENT DETAILS\n`;
      txt += `----------------------------------------------------\n`;
      txt += `• Bank Name: ${defaultBank.bankName}\n`;
      txt += `• Account Holder: ${defaultBank.accountHolderName}\n`;
      txt += `• Account Number: ${defaultBank.accountNumber}\n`;
      txt += `• IFSC Code: ${defaultBank.ifscCode}\n`;
      if (defaultBank.accountType)
        txt += `• Account Type: ${defaultBank.accountType.toUpperCase()}\n`;
      txt += `\n`;
    }

    // Terms / Policies
    if (paymentPolicy || refundPolicy || terms) {
      txt += `----------------------------------------------------\n`;
      txt += `7. POLICIES & TERMS\n`;
      txt += `----------------------------------------------------\n`;
      if (paymentPolicy) txt += `Payment Policy:\n${paymentPolicy}\n\n`;
      if (refundPolicy)
        txt += `Cancellation & Refund Policy:\n${refundPolicy}\n\n`;
      if (terms) txt += `Terms & Conditions:\n${terms}\n\n`;
    }

    // Sign off
    txt += `----------------------------------------------------\n`;
    txt += `YOUR DEDICATED TRAVEL SPECIALIST\n`;
    txt += `----------------------------------------------------\n`;
    txt += `Name: ${consultant.name}\n`;
    if (consultant.phone) txt += `Phone: ${consultant.phone}\n`;
    if (consultant.email) txt += `Email: ${consultant.email}\n`;
    txt += `\nThanks & Warm Regards,\n`;
    txt += `${companyName}\n`;
    if (companyPhone) txt += `Support Call: ${companyPhone}\n`;
    if (companyEmail) txt += `Email: ${companyEmail}\n`;
    if (companyWebsite) txt += `Website: ${companyWebsite}\n`;
    if (companyAddress) txt += `Address: ${companyAddress}\n`;

    return txt;
  };

  const handleCopyFormatted = async () => {
    try {
      const htmlEl = containerRef.current;
      if (!htmlEl) return;

      const htmlString = htmlEl.innerHTML;
      const plainString = generatePlainText();

      if (navigator.clipboard && window.ClipboardItem) {
        const blobHtml = new Blob([htmlString], { type: "text/html" });
        const blobText = new Blob([plainString], { type: "text/plain" });
        const item = new ClipboardItem({
          "text/html": blobHtml,
          "text/plain": blobText,
        });
        await navigator.clipboard.write([item]);
      } else {
        await navigator.clipboard.writeText(plainString);
      }

      setCopied(true);
      message.success("Formatted Email Quotation copied to clipboard!");
      setTimeout(() => setCopied(false), 3000);
    } catch (err) {
      console.error("Copy failed", err);
      try {
        await navigator.clipboard.writeText(generatePlainText());
        message.success("Plain text quotation copied to clipboard!");
      } catch (e) {
        message.error("Failed to copy quotation");
      }
    }
  };

  const handleCopyPlainText = async () => {
    try {
      await navigator.clipboard.writeText(generatePlainText());
      message.success("Plain text version copied!");
    } catch (err) {
      message.error("Failed to copy text");
    }
  };

  return (
    <Modal
      title={
        <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
          <MailOutlined style={{ color: "#1890ff", fontSize: 20 }} />
          <div>
            <div style={{ fontSize: 16, fontWeight: 700 }}>
              Email Version Quotation Preview
            </div>
            <div style={{ fontSize: 12, color: "#6b7280", fontWeight: 400 }}>
              Copy & paste directly into Gmail, Outlook, or Word
            </div>
          </div>
        </div>
      }
      open={open}
      onCancel={onClose}
      width={820}
      style={{ top: 20 }}
      footer={[
        <Button key="close" onClick={onClose} size="large">
          Close
        </Button>,
        <Button
          key="copyText"
          onClick={handleCopyPlainText}
          size="large"
          style={{ borderRadius: 8 }}
        >
          Copy Plain Text
        </Button>,
        <Button
          key="copyFormatted"
          type="primary"
          icon={copied ? <CheckOutlined /> : <CopyOutlined />}
          onClick={handleCopyFormatted}
          size="large"
          style={{
            borderRadius: 8,
            background: copied ? "#52c41a" : "#1890ff",
            borderColor: copied ? "#52c41a" : "#1890ff",
            fontWeight: 600,
          }}
        >
          {copied ? "Copied to Clipboard!" : "Copy Formatted Email"}
        </Button>,
      ]}
    >
      <Alert
        message="Ready to Email"
        description="Click 'Copy Formatted Email' to copy formatted rich text (tables, bold headings, lists) that you can paste straight into Gmail or Outlook."
        type="info"
        showIcon
        style={{ marginBottom: 16, borderRadius: 8 }}
      />

      {/* Main Email Container styled for HTML Copy */}
      <div
        ref={containerRef}
        style={{
          background: "#ffffff",
          border: "1px solid #e5e7eb",
          borderRadius: 8,
          padding: "24px 28px",
          maxHeight: "60vh",
          overflowY: "auto",
          fontFamily: "Arial, Helvetica, sans-serif",
          fontSize: "14px",
          color: "#1f2937",
          lineHeight: "1.6",
        }}
      >
        {/* HEADER BRANDING (Text-only) */}
        <div
          style={{
            borderBottom: "2px solid #1890ff",
            paddingBottom: "16px",
            marginBottom: "20px",
            display: "flex",
            justifyContent: "space-between",
            alignItems: "flex-start",
          }}
        >
          <div>
            <h2
              style={{
                margin: "0 0 4px 0",
                fontSize: "22px",
                color: "#111827",
                fontWeight: "bold",
              }}
            >
              {companyName}
            </h2>
            {companyAddress && (
              <div style={{ fontSize: "12px", color: "#6b7280" }}>
                {companyAddress}
              </div>
            )}
            <div style={{ fontSize: "12px", color: "#6b7280", marginTop: "2px" }}>
              {companyPhone && <span>Phone: {companyPhone} | </span>}
              {companyEmail && <span>Email: {companyEmail} | </span>}
              {companyWebsite && <span>Web: {companyWebsite}</span>}
            </div>
          </div>
          <div style={{ textAlign: "right" }}>
            <span
              style={{
                display: "inline-block",
                padding: "4px 12px",
                background: "#e6f7ff",
                color: "#1890ff",
                fontWeight: "bold",
                borderRadius: "4px",
                fontSize: "13px",
              }}
            >
              TOUR PROPOSAL
            </span>
            <div
              style={{
                fontSize: "12px",
                color: "#6b7280",
                marginTop: "4px",
              }}
            >
              Ref: {payload?.proposalId || "Q-" + Date.now().toString().slice(-6)}
            </div>
          </div>
        </div>

        {/* GREETING */}
        <div style={{ marginBottom: "20px" }}>
          <p style={{ margin: "0 0 10px 0", fontSize: "15px", fontWeight: "600" }}>
            Dear {payload?.customerName || "Valued Guest"},
          </p>
          <p style={{ margin: "0 0 10px 0", color: "#374151" }}>
            Warm greetings from <strong>{companyName}</strong>! Thank you for
            reposing your trust in us for your travel plans. We are excited to
            present your personalized itinerary for your journey to{" "}
            <strong style={{ color: "#1890ff" }}>{destName}</strong>.
          </p>
        </div>

        {/* JOURNEY OVERVIEW CARD */}
        <div
          style={{
            background: "#f9fafb",
            border: "1px solid #e5e7eb",
            borderRadius: "6px",
            padding: "16px",
            marginBottom: "20px",
          }}
        >
          <h3
            style={{
              margin: "0 0 12px 0",
              fontSize: "15px",
              color: "#111827",
              borderBottom: "1px solid #e5e7eb",
              paddingBottom: "6px",
            }}
          >
            📋 Journey Overview
          </h3>
          <table
            style={{
              width: "100%",
              borderCollapse: "collapse",
              fontSize: "13px",
            }}
          >
            <tbody>
              <tr>
                <td style={{ padding: "4px 0", color: "#6b7280", width: "35%" }}>
                  <strong>Guest Name:</strong>
                </td>
                <td style={{ padding: "4px 0", color: "#111827" }}>
                  {payload?.customerName || "-"}
                </td>
              </tr>
              <tr>
                <td style={{ padding: "4px 0", color: "#6b7280" }}>
                  <strong>Destination(s):</strong>
                </td>
                <td style={{ padding: "4px 0", color: "#111827", fontWeight: "600" }}>
                  {destName}
                </td>
              </tr>
              <tr>
                <td style={{ padding: "4px 0", color: "#6b7280" }}>
                  <strong>Duration:</strong>
                </td>
                <td style={{ padding: "4px 0", color: "#111827" }}>
                  {payload?.durationText ||
                    (days.length ? `${days.length - 1} Nights / ${days.length} Days` : "-")}
                </td>
              </tr>
              <tr>
                <td style={{ padding: "4px 0", color: "#6b7280" }}>
                  <strong>Travel Date:</strong>
                </td>
                <td style={{ padding: "4px 0", color: "#111827" }}>
                  {payload?.dateRange?.[0] || lead?.travelDate?.slice(0, 10) || "As per request"}
                </td>
              </tr>
              <tr>
                <td style={{ padding: "4px 0", color: "#6b7280" }}>
                  <strong>Travelers:</strong>
                </td>
                <td style={{ padding: "4px 0", color: "#111827" }}>
                  {payload?.travelers?.adults || 1} Adults
                  {payload?.travelers?.children ? `, ${payload.travelers.children} Children` : ""}
                  {payload?.travelers?.infants ? `, ${payload.travelers.infants} Infants` : ""}
                </td>
              </tr>
              <tr>
                <td style={{ padding: "6px 0 0 0", color: "#047857", fontSize: "14px" }}>
                  <strong>Grand Total Package Cost:</strong>
                </td>
                <td style={{ padding: "6px 0 0 0", color: "#047857", fontSize: "16px", fontWeight: "bold" }}>
                  ₹{Number(pricing?.grandTotal || 0).toLocaleString("en-IN")}
                </td>
              </tr>
            </tbody>
          </table>
        </div>

        {/* ACCOMMODATION / HOTEL DETAILS */}
        <div style={{ marginBottom: "20px" }}>
          <h3
            style={{
              margin: "0 0 10px 0",
              fontSize: "15px",
              color: "#111827",
              borderBottom: "1px solid #e5e7eb",
              paddingBottom: "6px",
            }}
          >
            🏨 Accommodation Details
          </h3>
          {days.some((d) => d.hotels && d.hotels.length > 0) ? (
            <table
              style={{
                width: "100%",
                borderCollapse: "collapse",
                fontSize: "13px",
                border: "1px solid #e5e7eb",
              }}
            >
              <thead>
                <tr style={{ background: "#f3f4f6", textAlign: "left" }}>
                  <th style={{ padding: "8px 10px", border: "1px solid #e5e7eb" }}>Day</th>
                  <th style={{ padding: "8px 10px", border: "1px solid #e5e7eb" }}>City</th>
                  <th style={{ padding: "8px 10px", border: "1px solid #e5e7eb" }}>Hotel & Rating</th>
                  <th style={{ padding: "8px 10px", border: "1px solid #e5e7eb" }}>Room Type</th>
                  <th style={{ padding: "8px 10px", border: "1px solid #e5e7eb" }}>Meal Plan</th>
                </tr>
              </thead>
              <tbody>
                {days.map((day, idx) => {
                  if (!day.hotels || day.hotels.length === 0) return null;
                  return day.hotels.map((h, hIdx) => (
                    <tr key={`${idx}-${hIdx}`}>
                      <td style={{ padding: "8px 10px", border: "1px solid #e5e7eb" }}>
                        Day {idx + 1}
                      </td>
                      <td style={{ padding: "8px 10px", border: "1px solid #e5e7eb" }}>
                        {day.city || day.title || "-"}
                      </td>
                      <td style={{ padding: "8px 10px", border: "1px solid #e5e7eb", fontWeight: "600" }}>
                        {h.name || h.hotelName || "Selected Hotel"} ({h.starRating || "3"}★)
                      </td>
                      <td style={{ padding: "8px 10px", border: "1px solid #e5e7eb" }}>
                        {h.roomCategory || h.roomType || "Standard"}
                      </td>
                      <td style={{ padding: "8px 10px", border: "1px solid #e5e7eb" }}>
                        {h.mealPlan || "CP (Breakfast)"}
                      </td>
                    </tr>
                  ));
                })}
              </tbody>
            </table>
          ) : (
            <p style={{ fontSize: "13px", color: "#6b7280", margin: 0 }}>
              Hotels as selected in final booking / As per package schedule.
            </p>
          )}
        </div>

        {/* DAY-WISE ITINERARY & VEHICLES */}
        <div style={{ marginBottom: "20px" }}>
          <h3
            style={{
              margin: "0 0 10px 0",
              fontSize: "15px",
              color: "#111827",
              borderBottom: "1px solid #e5e7eb",
              paddingBottom: "6px",
            }}
          >
            🗓️ Day-wise Itinerary & Transport
          </h3>
          {days.map((day, idx) => (
            <div
              key={idx}
              style={{
                marginBottom: "12px",
                padding: "10px 12px",
                background: "#f9fafb",
                borderRadius: "6px",
                borderLeft: "3px solid #1890ff",
              }}
            >
              <div style={{ fontWeight: "bold", color: "#111827", fontSize: "14px" }}>
                Day {idx + 1}: {day.title || `Day ${idx + 1}`}{" "}
                {day.city && <span style={{ color: "#6b7280", fontWeight: "normal" }}>({day.city})</span>}
              </div>

              {day.notes && (
                <div style={{ fontSize: "13px", color: "#374151", marginTop: "4px" }}>
                  {day.notes}
                </div>
              )}

              {day.transport && day.transport.length > 0 && (
                <div style={{ fontSize: "12px", color: "#1e40af", marginTop: "6px" }}>
                  <strong>🚗 Transport:</strong>{" "}
                  {day.transport.map((t, tIdx) => (
                    <span key={tIdx}>
                      {t.routeName || (t.source && t.destination ? `${t.source} → ${t.destination}` : "Sightseeing Transfer")}{" "}
                      (Vehicle: <strong>{t.vehicle || t.vehicleType || "Cab"}</strong>)
                      {tIdx < day.transport.length - 1 ? " | " : ""}
                    </span>
                  ))}
                </div>
              )}

              {day.activities && day.activities.length > 0 && (
                <div style={{ marginTop: "8px", paddingTop: "8px", borderTop: "1px dashed #e2e8f0" }}>
                  <strong style={{ fontSize: "12px", color: "#7e22ce" }}>🏄 Activities & Sightseeings:</strong>
                  <div style={{ marginTop: "6px", display: "flex", flexDirection: "column", gap: "8px" }}>
                    {day.activities.map((act, aIdx) => (
                      <div
                        key={aIdx}
                        style={{
                          display: "flex",
                          alignItems: "center",
                          gap: "10px",
                          background: "#ffffff",
                          padding: "8px 10px",
                          borderRadius: "6px",
                          border: "1px solid #f1f5f9",
                        }}
                      >
                        {(act.imageUrl || act.image) && (
                          <img
                            src={act.imageUrl || act.image}
                            alt={act.title}
                            style={{ width: "60px", height: "45px", objectFit: "cover", borderRadius: "6px" }}
                          />
                        )}
                        <div style={{ flex: 1 }}>
                          <div style={{ fontSize: "13px", fontWeight: "bold", color: "#0f172a" }}>
                            {act.title}
                          </div>
                          <div style={{ fontSize: "11px", color: "#64748b" }}>
                            Category: {act.category || "Sightseeing"} {act.duration && `• ⏱️ ${act.duration}`}
                          </div>
                        </div>
                        {act.price > 0 && (
                          <div style={{ textAlign: "right", fontSize: "12px", fontWeight: "bold", color: "#059669" }}>
                            ₹{Number(act.price).toLocaleString("en-IN")} / pax
                            {act.quantity > 1 && (
                              <div style={{ fontSize: "11px", color: "#64748b", fontWeight: "normal" }}>
                                {act.quantity} Pax = ₹{Number(act.total || act.price * act.quantity).toLocaleString("en-IN")}
                              </div>
                            )}
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>

        {/* INCLUSIONS & EXCLUSIONS */}
        <div style={{ marginBottom: "20px", display: "flex", gap: "16px", flexWrap: "wrap" }}>
          {inclusions.length > 0 && (
            <div style={{ flex: 1, minWidth: "280px" }}>
              <h4 style={{ margin: "0 0 8px 0", color: "#047857", fontSize: "14px" }}>
                ✅ Package Inclusions
              </h4>
              <ul style={{ margin: 0, paddingLeft: "18px", fontSize: "13px", color: "#374151" }}>
                {inclusions.map((inc, i) => (
                  <li key={i} style={{ marginBottom: "4px" }}>
                    {inc}
                  </li>
                ))}
              </ul>
            </div>
          )}

          {exclusions.length > 0 && (
            <div style={{ flex: 1, minWidth: "280px" }}>
              <h4 style={{ margin: "0 0 8px 0", color: "#b91c1c", fontSize: "14px" }}>
                ❌ Package Exclusions
              </h4>
              <ul style={{ margin: 0, paddingLeft: "18px", fontSize: "13px", color: "#374151" }}>
                {exclusions.map((exc, i) => (
                  <li key={i} style={{ marginBottom: "4px" }}>
                    {exc}
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>

        {/* BANK ACCOUNT DETAILS */}
        {defaultBank && (
          <div
            style={{
              background: "#eff6ff",
              border: "1px solid #bfdbfe",
              borderRadius: "6px",
              padding: "14px 16px",
              marginBottom: "20px",
            }}
          >
            <h3
              style={{
                margin: "0 0 8px 0",
                fontSize: "14px",
                color: "#1e3a8a",
              }}
            >
              🏦 Bank Payment Details
            </h3>
            <div style={{ fontSize: "13px", color: "#1e40af" }}>
              <div>
                <strong>Bank Name:</strong> {defaultBank.bankName}
              </div>
              <div>
                <strong>Account Holder Name:</strong> {defaultBank.accountHolderName}
              </div>
              <div>
                <strong>Account Number:</strong> {defaultBank.accountNumber}
              </div>
              <div>
                <strong>IFSC Code:</strong> {defaultBank.ifscCode}
              </div>
              {defaultBank.accountType && (
                <div>
                  <strong>Account Type:</strong> {defaultBank.accountType.toUpperCase()}
                </div>
              )}
            </div>
          </div>
        )}

        {/* TERMS & POLICIES */}
        {(paymentPolicy || refundPolicy || terms) && (
          <div style={{ marginBottom: "20px", fontSize: "12px", color: "#4b5563" }}>
            <h3
              style={{
                margin: "0 0 8px 0",
                fontSize: "14px",
                color: "#111827",
                borderBottom: "1px solid #e5e7eb",
                paddingBottom: "4px",
              }}
            >
              📜 Policies & Terms
            </h3>
            {paymentPolicy && (
              <div style={{ marginBottom: "8px" }}>
                <strong>Payment Policy:</strong> {paymentPolicy}
              </div>
            )}
            {refundPolicy && (
              <div style={{ marginBottom: "8px" }}>
                <strong>Cancellation Policy:</strong> {refundPolicy}
              </div>
            )}
            {terms && (
              <div>
                <strong>Terms & Conditions:</strong> {terms}
              </div>
            )}
          </div>
        )}

        {/* ASSIGNED CONSULTANT & SIGN-OFF */}
        <div
          style={{
            borderTop: "2px solid #e5e7eb",
            paddingTop: "16px",
            marginTop: "20px",
          }}
        >
          <div style={{ fontWeight: "600", fontSize: "14px", color: "#111827" }}>
            Your Dedicated Travel Specialist:
          </div>
          <div style={{ fontSize: "13px", color: "#374151", marginTop: "2px" }}>
            <strong>{consultant.name}</strong>
            {consultant.phone && <span> | Phone: {consultant.phone}</span>}
            {consultant.email && <span> | Email: {consultant.email}</span>}
          </div>

          <p style={{ marginTop: "16px", marginBottom: "4px", fontSize: "13px", color: "#6b7280" }}>
            Thanks & Warm Regards,
          </p>
          <div style={{ fontWeight: "bold", fontSize: "15px", color: "#111827" }}>
            {companyName}
          </div>
          {companyPhone && (
            <div style={{ fontSize: "12px", color: "#6b7280" }}>
              Helpline: {companyPhone}
            </div>
          )}
          {companyEmail && (
            <div style={{ fontSize: "12px", color: "#6b7280" }}>
              Email: {companyEmail}
            </div>
          )}
          {companyWebsite && (
            <div style={{ fontSize: "12px", color: "#6b7280" }}>
              Website: {companyWebsite}
            </div>
          )}
        </div>
      </div>
    </Modal>
  );
};

export default EmailQuoteModal;
